import { DeepValues } from '../types/DeepValues';

const USERS = 'users';

// ID parameter names to use in form routes. Most can use the generic ID, but for deeply nested routes we need unique parameter names
export const ID_PARAMS = {
  ANY: ':id',
  ORG: ':orgId',
  USER: ':userId',
} as const;

export type IdParamsType = DeepValues<typeof ID_PARAMS>;

export function cleanFormIdParam(idParamName: IdParamsType): string {
  // remove the leading `:` from the parameter name
  return (idParamName as string).slice(1);
}

// Base paths
export const BASE = {
  ADMINISTRATION: '/administration',
  ALERTS: '/alerts',
  AUTH: {
    CHANGE_EXPIRED_PASSWORD: '/login/change-expired-password',
    FORGOT_PASSWORD: '/login/forgotpassword',
    LOGIN: '/login',
    RESET_PASSWORD: '/login/resetpassword',
    SSO: '/sso',
    VERIFY_EMAIL: '/login/verify-email',
    WELCOME: `/welcome/${ID_PARAMS.ANY}`,
  },
  BILL_AUTOMATION: '/bill-automation',
  COST: '/cost',
  CUSTOM_DASHBOARDS: '/custom-dashboards',
  EV_CHARGING: '/evcharging',
  IMAGES: {
    BILL: `/bill-image/${ID_PARAMS.ANY}`,
    STATEMENT: `/statement-image/${ID_PARAMS.ANY}`,
  },
  INTERVAL_DATA_ELECTRIC: '/electric',
  INTERVAL_DATA_NATURAL_GAS: '/naturalgas',
  INTERVAL_DATA_PORTFOLIO: '/portfolio',
  INTERVAL_DATA_WATER: '/water',
  REPORTS: '/reports',
  ROOT: '/',
  USER_SETTINGS: '/user-settings',
  SMART_METERS: '/smartmeters',
  SUMMARY: '/summary',
  SUPPORT: '/support',
  SYSTEM_ADMINISTRATION: '/systemadministration',
  USAGE: '/usage',
} as const;

export type BaseRouteType = DeepValues<typeof BASE>;

export const NESTED_ROUTES = {
  ADMINISTRATION: {
    ACCOUNTS: 'accounts',
    ALLOCATION_TEMPLATE: 'allocation-settings',
    BILLS: 'bills',
    BUDGETS: 'budgets',
    ENERGY_ASSETS: 'energyassets',
    FACILITIES: 'facilities',
    FACILITY_GROUPS: 'facilitygroups',
    METERS: 'meters',
    ORGANIZATION: 'organization',
    PROJECTS: 'projects',
    UTILITIES: 'utilities',
    WORK_ORDERS: 'workorders',
    ORG_USERS: `${USERS}/${ID_PARAMS.ANY}`,
  },
  BILL_AUTOMATION: {
    CREDENTIALS: 'credentials',
    BILL_UPLOAD: 'bill-upload',
    SPREADSHEET_UPLOAD: 'spreadsheet-upload',
    SUMMARY: 'billautomationsummary',
    PREVIEW: 'billautomationpreview',
  },
  COST: {
    ALL_UTILITIES: 'costallutilities',
    BUDGETS_ACTUALS: 'budgetsactuals',
    BUDGET_REVIEW: 'budgetreview',
    LINE_ITEM_COST: 'lineitemcost',
    UNIT_ECONOMICS: 'uniteconomics',
    WEATHER_NORMALIZED_COST: 'weathernormalizedcost',
    YEAR_OVER_YEAR: 'costyoy',
    YEAR_OVER_YEAR_BAR: 'costyoybar',
  },
  CUSTOM_DASHBOARDS: {
    MY_DASHBOARDS: 'my-dashboards',
  },
  EV_CHARGING: {
    FACILITY: 'facilitysummary',
    SESSION: 'sessionsummary',
    STATION: 'stationsummary',
  },
  INTERVAL_DATA_ELECTRIC: {
    HEATMAP: 'heatmap',
    MAP: 'map',
    METRICS: 'metrics',
    PROFILES: 'profiles',
    POINTS: 'points',
    TRENDS: 'trends',
    USAGE: 'usage',
  },
  INTERVAL_DATA_NATURAL_GAS: {
    HEATMAP: 'heatmap',
    MAP: 'map',
    POINTS: 'points',
    USAGE: 'usage',
  },
  INTERVAL_DATA_PORTFOLIO: {
    ALL_POINTS: 'allpoints',
    METERS: 'meters',
    POINTS: 'points',
    SITES: 'sites',
  },
  INTERVAL_DATA_WATER: {
    HEATMAP: 'heatmap',
    MAP: 'map',
    POINTS: 'points',
    USAGE: 'usage',
  },
  REPORTS: {
    ACCOUNT_DETAILS: 'accountdetails',
    BILLS: 'reportbills',
    CALENDAR_NORMALIZED: 'reportcalendarnormalized',
    FACILITIES: 'reportfacilities',
    HISTORICAL_USAGE_COST: 'historicalusagecost',
    LINE_ITEMS: 'lineitems',
    TOTAL_ENERGY_COSTS: 'totalenergycosts',
  },
  SMART_METERS: {
    BASELINES: 'smartmeterbaselines',
    HOURLY: 'smartmeterhourly',
    MONTHLY: 'smartmetermonthly',
    WEEKLY: 'smartmeterweekly',
  },
  SUMMARY: {
    DATA_COMPLETENESS: 'datacompleteness',
    DATA_INSIGHTS: 'datainsights',
    DATA_RECENCY: 'datarecency',
    EMISSIONS: 'emissions',
    ENERGY_STAR: 'energystar',
    HOMEPAGE: 'homepage',
    OPPENGINE: 'oppengine',
  },
  SUPPORT: {
    CONTACT_US: 'contactus',
    FAQ: 'faq',
  },
  SYSTEM_ADMINISTRATION: {
    ORGANIZATIONS: 'organizations',
    ORGANIZATION_GROUPS: 'organizationgroups',
    USERS,
    ORG_USERS: `${USERS}/${ID_PARAMS.USER}`,
  },
  USAGE: {
    ALL_UTILITIES: 'usageallutilities',
    DEMAND: 'demand',
    ELECTRIC: 'usageelectric',
    ENERGY_KBTU: 'usageenergykbtu',
    GAS: 'usagegas',
    INVERSE_MODELS: 'inversemodels',
    PROJECT_SAVINGS: 'projectsavings',
    TEMPERATURE: 'usagetemperature',
    WATER: 'usagewater',
    WEATHER: 'usageweather',
    WEATHER_NORMALIZED_USAGE: 'weathernormalizedusage',
    YEAR_OVER_YEAR: 'usageyoy',
    YEAR_OVER_YEAR_BAR: 'usageyoybar',
  },
  USERS,
} as const;

export type NestedRoutesType = DeepValues<typeof NESTED_ROUTES>;
